import moment from "moment";
import React, { FC } from "react";
import { useNavigate } from "react-router-dom";

import { useGetMySignaturesQuery } from "../../../slices/PortalSlice";

/** Portal home: documents waiting for the client's signature (hidden when there are none). */
export const PortalSignaturesCard: FC<{ customerId: string }> = ({
  customerId,
}) => {
  const navigate = useNavigate();
  const { data } = useGetMySignaturesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const list = (data ?? []).filter((r) => r.customerId === customerId);
  const toSign = list.filter((r) => r.canSign);
  const waiting = list.filter((r) => !r.canSign && r.status === "sent");
  const finished = list.filter((r) => r.status === "completed").slice(0, 3);
  if (!toSign.length && !waiting.length && !finished.length) return null;

  return (
    <section
      className={
        "flex flex-col gap-3 rounded-[14px] border border-[#E8E8EC] bg-white p-[18px] sm:p-6"
      }>
      <h2 className={"m-0 text-base font-extrabold sm:text-[17px]"}>
        {toSign.length ? "Waiting for your signature" : "Signatures"}
      </h2>
      <ul className={"m-0 flex list-none flex-col gap-2 p-0"}>
        {toSign.map((r) => (
          <li
            key={r.id}
            className={
              "flex flex-wrap items-center gap-3 rounded-[12px] border-[1.5px] border-[#D9D1FF] bg-[#F7F5FF] px-4 py-3"
            }>
            <div className={"flex min-w-0 flex-1 flex-col"}>
              <span className={"truncate text-sm font-extrabold"}>
                {r.documentName}
              </span>
              <span className={"text-[12.5px] font-semibold text-[#6B7185]"}>
                From {r.sentBy || r.firmName} ·{" "}
                {moment(r.sentAt).format("MMM D")}
                {r.signBy
                  ? ` · please sign by ${moment(r.signBy).format("MMM D")}`
                  : ""}
              </span>
            </div>
            <button
              type={"button"}
              onClick={() => navigate(`/portal/signatures/${r.id}`)}
              className={
                "h-10 rounded-[9px] bg-[#6B4EFF] px-4 text-[13.5px] font-extrabold text-white hover:bg-[#5B3DF5]"
              }>
              Review and sign
            </button>
          </li>
        ))}
        {waiting.map((r) => (
          <li
            key={r.id}
            className={
              "flex flex-col rounded-[12px] border border-[#EDEFF3] px-4 py-3"
            }>
            <span className={"truncate text-sm font-bold"}>
              {r.documentName}
            </span>
            <span className={"text-[12.5px] font-semibold text-[#6B7185]"}>
              {r.signers.find((s) => s.isMe)?.status === "signed"
                ? "You signed · "
                : ""}
              {r.statusText}
            </span>
          </li>
        ))}
        {finished.map((r) => (
          <li
            key={r.id}
            className={
              "flex flex-col rounded-[12px] border border-[#EDEFF3] px-4 py-3"
            }>
            <span className={"truncate text-sm font-bold"}>
              {r.documentName}
            </span>
            <span className={"text-[12.5px] font-semibold text-[#12805C]"}>
              Signed by everyone{" "}
              {r.completedAt
                ? `on ${moment(r.completedAt).format("MMM D")}`
                : ""}{" "}
              · the signed copy is in “Shared with you”
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};
